import { isImmutableRecord } from 'app/common/utils/immutable-type-util';
import { type ChangeEvent, type ChangeEventHandler, useCallback } from 'react';
import type { WidgetProps } from '../../../types.js';
import { getWidgetId, useWidgetTitle } from '../../../util/widget.js';

export type NumberWidgetHookProps = {
  label: string;
  value: string;
  description?: string;
  step?: number;
  min?: number;
  max?: number;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  id: string;
} & WidgetProps;

export const useNumberWidget = (props: WidgetProps) => {
  const { schema, value, onChange, onBlur } = props;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value === '' ? undefined : Number(event.target.value));
      onBlur();
    },
    [onBlur, onChange]
  );

  const label = useWidgetTitle(props);
  const id = getWidgetId(props);
  const step = schema.get('multipleOf');

  let min = schema.get('minimum');

  const exclusiveMin = schema.get('exclusiveMinimum');
  if (exclusiveMin !== undefined) {
    min = exclusiveMin + (step ?? 0);
  }

  let max = schema.get('maximum');

  const exclusiveMax = schema.get('exclusiveMaximum');
  if (exclusiveMax !== undefined) {
    max = exclusiveMax - (step ?? 0);
  }

  return {
    ...props,
    label,
    value: String(isImmutableRecord(value) ? value.toJS() : value),
    description: schema.get('description'),
    step,
    min,
    max,
    handleChange,
    id
  };
};
